import { type ButtonHTMLAttributes, forwardRef } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: Variant;
	size?: Size;
	loading?: boolean;
	fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
	primary:
		"bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white shadow-sm disabled:bg-muted disabled:text-muted-foreground",
	secondary: "bg-muted hover:bg-border active:bg-border text-foreground",
	ghost:
		"bg-transparent hover:bg-muted active:bg-muted text-muted-foreground hover:text-foreground",
	danger: "bg-red-500 hover:bg-red-600 active:bg-red-700 text-white shadow-sm",
};

const sizeClasses: Record<Size, string> = {
	sm: "h-8 px-3 text-sm rounded-md",
	md: "h-10 px-4 text-sm rounded-lg",
	lg: "h-12 px-6 text-base rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	function Button(
		{
			variant = "primary",
			size = "md",
			loading,
			fullWidth,
			className = "",
			children,
			disabled,
			...props
		},
		ref,
	) {
		return (
			<button
				ref={ref}
				disabled={disabled || loading}
				// Announce the in-flight state to assistive tech (F4.5) — a spinner is
				// visual-only; aria-busy tells a screen-reader the action is processing.
				aria-busy={loading || undefined}
				className={`inline-flex items-center justify-center gap-2 font-medium transition duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100 ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? "w-full" : ""} ${className}`}
				{...props}
			>
				{loading && (
					<span
						className="size-4 rounded-full border-2 border-current border-t-transparent animate-spin"
						aria-hidden
					/>
				)}
				{children}
			</button>
		);
	},
);
